import { useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { useLocation } from 'wouter';
import { useSwipe } from '@/hooks/useSwipe';
import { useLongPress } from '@/hooks/useLongPress';
import { TagBadge } from '@/components/shared/TagBadge';
import { StatusDot } from '@/components/shared/StatusDot';
import { ContextMenu } from './ContextMenu';
import { TAG_COLORS } from '@/lib/constants';
import { formatTime } from '@/lib/utils';
import type { WeddingEvent, EventStatus } from '@/lib/types';

interface EventRowProps {
  event: WeddingEvent;
  onDelete: (eventId: string) => void;
  onStatusChange: (eventId: string, status: EventStatus) => void;
  onEdit: (eventId: string) => void;
}

export function EventRow({ event, onDelete, onStatusChange, onEdit }: EventRowProps) {
  const [, navigate] = useLocation();
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [isTouching, setIsTouching] = useState(false);

  const handleSwipeLeft = useCallback(() => {
    // Revealed — user can tap red area to delete
  }, []);

  const { swipeRef, swipeOffset, isRevealed, resetSwipe, swipeHandlers } = useSwipe(
    handleSwipeLeft,
    80
  );

  const longPressHandlers = useLongPress(() => {
    setContextMenuOpen(true);
  }, 300);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      setIsTouching(true);
      swipeHandlers.onTouchStart(e);
      longPressHandlers.onTouchStart(e);
    },
    [swipeHandlers, longPressHandlers]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      // Cancel long press if user starts swiping
      longPressHandlers.onTouchMove();
      swipeHandlers.onTouchMove(e);
    },
    [swipeHandlers, longPressHandlers]
  );

  const handleTouchEnd = useCallback(() => {
    setIsTouching(false);
    longPressHandlers.onTouchEnd();
    swipeHandlers.onTouchEnd();
  }, [swipeHandlers, longPressHandlers]);

  const handleRowClick = useCallback(() => {
    // Only navigate if not swiped open
    if (swipeOffset === 0 && !isRevealed) {
      onEdit(event.id);
    }
  }, [swipeOffset, isRevealed, onEdit, event.id]);

  const handleDeleteTap = useCallback(() => {
    onDelete(event.id);
    resetSwipe();
  }, [event.id, onDelete, resetSwipe]);

  const categoryColor = TAG_COLORS[event.category];
  const showDeleteBg = isRevealed || swipeOffset < 0;

  return (
    <>
      {/* Outer wrapper */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {/* Red delete background layer */}
        {showDeleteBg && (
          <button
            onClick={handleDeleteTap}
            aria-label="Delete event"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: 80,
              background: 'rgba(248,113,113,0.15)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 0,
            }}
          >
            <Trash2 size={20} style={{ color: 'var(--color-error)' }} />
          </button>
        )}

        {/* Main row — slides via swipeOffset */}
        <div
          ref={swipeRef}
          role="button"
          tabIndex={0}
          onClick={handleRowClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleRowClick();
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            minHeight: 64,
            paddingTop: 12,
            paddingBottom: 12,
            paddingRight: 16,
            paddingLeft: 8,
            position: 'relative',
            zIndex: 1,
            background: 'var(--color-surface)',
            transform: `translateX(${swipeOffset}px)`,
            transition: isTouching ? 'none' : 'transform 200ms ease',
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          {/* Left color bar */}
          <div
            style={{
              width: 3,
              alignSelf: 'stretch',
              background: categoryColor.text,
              borderRadius: 2,
              marginRight: 10,
              flexShrink: 0,
            }}
          />

          {/* Time column */}
          <div
            style={{
              width: 60,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--color-text)',
                lineHeight: 1.2,
              }}
            >
              {formatTime(event.startTime)}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--color-text-muted)',
                lineHeight: 1.2,
              }}
            >
              {formatTime(event.endTime)}
            </span>
          </div>

          {/* Center column — name + location */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--color-text)',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {event.name}
            </span>
            {event.location && (
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {event.location}
              </span>
            )}
          </div>

          {/* Right column — TagBadge + StatusDot */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'center',
              gap: 4,
              marginLeft: 8,
              flexShrink: 0,
            }}
          >
            <TagBadge category={event.category} />
            <StatusDot status={event.status} size={8} />
          </div>
        </div>
      </div>

      {/* Context menu bottom sheet */}
      <ContextMenu
        event={event}
        open={contextMenuOpen}
        onOpenChange={(open) => {
          setContextMenuOpen(open);
          if (!open) resetSwipe();
        }}
        onEdit={() => {
          setContextMenuOpen(false);
          navigate(`/schedule/event/${event.id}/edit`);
        }}
        onSkip={() => {
          onStatusChange(event.id, 'skipped');
          setContextMenuOpen(false);
        }}
        onComplete={() => {
          onStatusChange(event.id, 'completed');
          setContextMenuOpen(false);
        }}
        onDelete={() => {
          onDelete(event.id);
          setContextMenuOpen(false);
        }}
      />
    </>
  );
}
